import { type JSX, useState } from 'react';

import { TprmQuestionnaireApi } from 'Api/TPRM/TprmQuestionnaireApi';
import { Button } from 'Components/Buttons/Buttons';
import { Checkbox } from 'Components/Checkbox/Checkbox';
import { FormFieldTextArea } from 'Components/FormField/FormFieldTextArea/FormFieldTextArea';
import { ConfirmationModal, ConfirmationModalOperationType } from 'Components/Modal/ConfirmationModal/ConfirmationModal';
import { Text } from 'Components/Text/Text';
import { ICON_SUBMIT } from 'Config/Icons';
import { AddDiscussionMessageRequest, Discussion, DiscussionMessageClient } from 'Models/DueDiligence';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { ResponseModel } from 'Models/ResponseModel';
import { PublicAddDiscussionMessageRequest, PublicDiscussion, PublicDiscussionMessage, PublicQuestionnaire } from 'Models/TPRM';
import { TprmOrUcrmDueDiligenceAssessment } from 'Models/UcrmTprm';
import { UserResponse } from 'Models/User';

import styles from './DiscussionThread.module.css';
import { MessageBubble } from '../MessageBubble/MessageBubble';

interface DiscussionThreadBaseProps {
    clientName: string;
    /** The message being composed, owned by the parent so that users can switch between assessment/questionnaire sidebar sections without losing partially composed messages. */
    draftMessage: string;
    onDraftMessageChange: (draftMessage: string) => void;
    organizationName: string;
}

interface DiscussionThreadAssessmentProps<T extends TprmOrUcrmDueDiligenceAssessment> extends DiscussionThreadBaseProps {
    perspective: 'assessment';
    assessment: T;
    /** Satisfied by `TprmAssessmentApi` and `UcrmAssessmentApi`, each of which returns its own module's assessment type. */
    assessmentApi: { addDiscussionMessage: (organizationId: string, assessmentSubjectId: string, assessmentId: string, request: AddDiscussionMessageRequest) => Promise<ResponseModel<T>> };
    discussion: Discussion;
    module: DueDiligenceModule;
    onMessageSent: (updatedAssessment: T) => void;
    users: UserResponse[];

    disabled?: boolean;
    /** If true, the option to reopen a submitted questionnaire when sending a message is not offered. Used where the sender is a questionnaire respondent rather than an assessor. */
    hideReopenQuestionnaireOption?: boolean;
}

interface DiscussionThreadQuestionnaireProps extends DiscussionThreadBaseProps {
    perspective: 'questionnaire';
    discussion: PublicDiscussion;
    questionnaire: PublicQuestionnaire;
    questionnaireApi: TprmQuestionnaireApi;
    onMessageSent: (updatedQuestionnaire: PublicQuestionnaire) => void;

    disabled?: boolean;
}

export type DiscussionThreadProps<T extends TprmOrUcrmDueDiligenceAssessment = TprmOrUcrmDueDiligenceAssessment> = DiscussionThreadAssessmentProps<T> | DiscussionThreadQuestionnaireProps;

/** Renders a list of messages between SummIT users and questionnaire respondents (third-party contacts in TPRM, or respondents in UCRM). Looks like a stereotypical chat app interface, where the "current" party's messages are on the right side and the other party's messages are on the left side. */
export const DiscussionThread = <T extends TprmOrUcrmDueDiligenceAssessment>(props: DiscussionThreadProps<T>): JSX.Element => {
    const questionnaireIsClosed = props.perspective === 'assessment' ? !!props.assessment.questionnaire_submitted_timestamp || !!props.assessment.assessment_submitted_timestamp : false;
    const canReopenQuestionnaire = props.perspective === 'assessment' && questionnaireIsClosed && !props.hideReopenQuestionnaireOption;

    const [reopenQuestionnaire, setReopenQuestionnaire] = useState(canReopenQuestionnaire);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const handleSendClick = () => {
        setShowConfirmModal(true);
    };

    const performSend = async (): Promise<string> => {
        const trimmedText = props.draftMessage.trim();

        if (props.perspective === 'assessment') {
            const request: AddDiscussionMessageRequest = {
                text: trimmedText,
                reopen_questionnaire: reopenQuestionnaire,
            };
            const response = await props.assessmentApi.addDiscussionMessage(props.assessment.third_party_id, props.assessment.service_id, props.assessment.id, request);
            props.onDraftMessageChange('');
            setReopenQuestionnaire(false);
            props.onMessageSent(response.data);
            return 'Message sent.';
        } else {
            const request: PublicAddDiscussionMessageRequest = {
                text: trimmedText,
            };
            const response = await props.questionnaireApi.addDiscussionMessage(props.questionnaire.third_party_id, props.questionnaire.service_id, props.questionnaire.id, request);
            props.onDraftMessageChange('');
            props.onMessageSent(response.data);
            return 'Message sent.';
        }
    };

    const hasInput = props.draftMessage.trim().length > 0;

    return (
        <>
            {showConfirmModal && (
                <ConfirmationModal operationType={ConfirmationModalOperationType.NON_DESTRUCTIVE} headerText="Send Message" areYouSureText="Are you sure you want to send this message?" buttonText="Send" buttonLoadingText="Sending..." buttonIcon={ICON_SUBMIT} performOperation={performSend} hideModal={() => setShowConfirmModal(false)}>
                    <Text>{props.perspective === 'assessment' ? props.organizationName : props.clientName} will be notified of your message.</Text>
                    {canReopenQuestionnaire && <Checkbox checked={reopenQuestionnaire} label="Reopen questionnaire" onToggled={setReopenQuestionnaire} tooltip={`If checked, the submission of the questionnaire will be undone and the ${props.perspective === 'assessment' && props.module === ProductModule.UCRM ? 'respondents' : 'third party'} will be able to make further changes.`} />}
                </ConfirmationModal>
            )}
            <div className={styles.container}>
                {props.discussion.messages.length > 0 && <div className={styles.messagesContainer}>{props.discussion.messages.map((message) => (props.perspective === 'assessment' ? <MessageBubble key={message.timestamp} perspective="assessment" clientName={props.clientName} message={message as DiscussionMessageClient} organizationName={props.organizationName} users={props.users} /> : <MessageBubble key={message.timestamp} perspective="questionnaire" clientName={props.clientName} message={message as PublicDiscussionMessage} organizationName={props.organizationName} />))}</div>}
                {!props.disabled && (
                    <div className={styles.inputArea}>
                        <div className={styles.textAreaWrapper}>
                            <FormFieldTextArea formFieldId="sendMessage" value={props.draftMessage} handleChange={(event: React.FormEvent<HTMLInputElement>) => props.onDraftMessageChange(event.currentTarget.value)} placeholder={`Send a message to ${props.perspective === 'assessment' ? props.organizationName : props.clientName}`} />
                        </div>
                        <Button variant="primary" onClick={handleSendClick} fontAwesomeImage={ICON_SUBMIT} disabled={!hasInput}>
                            Send
                        </Button>
                    </div>
                )}
            </div>
        </>
    );
};
